import { flowExcelStore } from '@/store/flowExcelStore';
import { RULE_TYPES } from '@/types/ruleType';
import { getUserSignatureUrl } from './signature';

/**
 * 转换ID键为名称键
 * @param key
 * @param suffix
 * @returns
 */
export function idKeyToNameKey(key, suffix = 'Name') {
  return key.replace(/Id(@|$)/g, `${suffix}$1`);
}

/**
 * 替换字段名
 * @param str
 * @param newFieldName
 * @returns
 */
export function replaceFieldName(str, newFieldName) {
  // 规则：把末尾段的 `id` / `Id` / `uid` / `Uid` 后缀剥掉（大小写敏感），
  // 保留段首字母大小写，再拼上 newFieldName。
  //  - 'dept@0'      + 'Name' → 'deptName@0'    （剥无）
  //  - 'deptId@0'    + 'Name' → 'deptName@0'    （剥 Id，保留 d）
  //  - 'deptUid@0'   + 'Name' → 'deptName@0'    （剥 Uid，保留 d）
  //  - 'deptUID@0'   + 'Name' → 'deptUName@0'   （剥 UID —— 不在四种里）
  //  - 'deptIdCard'  + 'Name' → 'deptIdCardName'（中间含 id 不剥）
  //  - 'string.deptId' + 'Name' → 'string.deptName'（保留前缀）
  const m = /(.*\.)?([^.@]+)(@\d+)?$/.exec(str);
  if (!m) return str + newFieldName;
  const [, prefix, field, suffix] = m;
  const stripped = field.replace(/(?:id|uid|Id|Uid)$/, '');
  return (prefix || '') + stripped + newFieldName + (suffix || '');
}

// 表单组件映射
export const formMap = {
  // 文本
  [RULE_TYPES.STRING.value]: (name, rule, field) => {
    if (field.rules?.default) {
      flowExcelStore.form?.setFieldValue(name, field.rules?.default || '');
    }
    return `<Form.Item rules={${rule}} className='mb0' field='${name}'><Input disabled={${field.disabled}} size='small' /></Form.Item>`;
  },
  // 长文本
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.TEXT.value]: (name, rule, field) => {
    const maxLength = field?.rules?.maxLength || 1000;
    const disabled = field?.disabled || false;
    const isSelectTemplate = field?.extInfo?.isSelectTemplate || false;
    const values = JSON.stringify(field?.values || []);
    // if (field.rules?.default) {
    //   store.form?.setFieldValue(name, field.rules?.default);
    // }

    return `
      <Form.Item
        rules={${rule}}
        className='mb0 lx-textarea'
        field='${name}'
      >
      <LongText
        options={${values}}
        isSelectTemplate={${isSelectTemplate}}
        maxLength={${maxLength}}
        disabled={${disabled}} />
      </Form.Item>`;
  },
  // 数值
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.INTEGER.value]: (name, rule, field) => {
    return `
      <Form.Item
        rules={${rule}}
        className='mb0'
        field='${name}'
      >
      <LxInputNumber
        name='${name}'
        disabled={${field.disabled}}
        max='${field.rules?.maximum}'
        min='${field.rules?.minimum}'
        size='small'
      />
      </Form.Item>`;
  },
  // 小数
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.FLOAT.value]: (name, rule, field) => {
    return `
      <Form.Item
        rules={${rule}}
        className='mb0'
        field='${name}'
      >
      <LxInputNumber
        name='${name}'
        disabled={${field.disabled}}
        max='${field.rules?.maximum}'
        min='${field.rules?.minimum}'
        precision='${field.extInfo?.precision}'
        size='small'
        onChange={${field?.onChange}}
      />
      </Form.Item>`;
  },
  // 人名
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.NAME.value]: (name, rule) => {
    const nameKey = idKeyToNameKey(name, 'Name');
    return `
      <Form.Item hidden={true} rules={${rule}} className='mb0' field='${name}'>
        <Input disabled={true} size='small' />
      </Form.Item>
      <Form.Item className='mb0' field='${nameKey}'>
        <Input disabled={true} size='small' />
      </Form.Item>
    `;
  },
  // 人名图片
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.NI.value]: (name, rule) => {
    const nameKey = idKeyToNameKey(name, 'Name');
    return `
      <Form.Item hidden={true} rules={${rule}} className='mb0' field='${name}'>
        <Input disabled={true} size='small' />
      </Form.Item>
      <Form.Item className='mb0 lx-signature-item' field='${nameKey}'>
        <Input disabled={true} size='small' />
      </Form.Item>
    `;
  },
  // 日期
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.DATE.value]: (name, rule, field) => {
    console.log('------DATE------>', name);
    const format = field?.rules?.format || 'YYYY-MM-DD';
    const picker = format === 'YYYY' ? 'year' : undefined;
    const showTime = format === 'YYYY-MM-DD HH:mm:ss' || format === 'YYYY-MM-DD HH:mm';
    return `
      <Form.Item
        rules={${rule}}
        className='mb0'
        field='${name}'
      >
      <DatePicker
        name='${name}'
        disabled={${field.disabled}}
        showTime={${showTime}}
        placeholder=''
        size='small'
        inputFormat='${format}'
        format='${format}'
        picker='${picker}'
        bordered={false}
        onChange={${field?.onChange}}
      />
      </Form.Item>`;
  },
  // 时间
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.TIME.value]: (name, rule, field) => {
    console.log('------TIME------>', name);
    return `
      <Form.Item
        rules={${rule}}
        className='mb0'
        field='${name}'
      >
      <TimePicker
        name='${name}'
        disabled={${field.disabled}}
        size='small'
        bordered={false}
        onChange={${field?.onChange}}
      />
      </Form.Item>`;
  },
  // 日期时间组件
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.DATETIME.value]: (name, rule, field) => {
    const format = field?.rules?.format || 'YYYY-MM-DD HH:mm:ss';
    const picker = format === 'YYYY' ? 'year' : undefined;
    const showTime = format === 'YYYY-MM-DD HH:mm:ss' || format === 'YYYY-MM-DD HH:mm';
    return `
      <Form.Item
        rules={${rule}}
        className='mb0'
        field='${name}'
      >
      <DatePicker
        name='${name}'
        disabled={${field.disabled}}
        showTime={${showTime}}
        placeholder=''
        size='small'
        inputFormat='${format}'
        format='${format}'
        picker='${picker}'
        bordered={false}
        onChange={${field?.onChange}}
      />
      </Form.Item>`;
  },
  // 列表
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.LIST.value]: (name, rule, field) => {
    const {
      source
    } = field;
    const extraKeyName = replaceFieldName(name, 'Name');
    return `
    <Form.Item hidden={true} className='mb0' field='${extraKeyName}'>
      <Input disabled={true} size='small' />
    </Form.Item>
    <Form.Item
      rules={${rule}}
      className='mb0'
      field='${name}'
    >
      <ListSelector
        name='${name}'
        disabled={${field.disabled}}
        placeholder=''
        source={${source ? source : JSON.stringify({})}}
        onChange={${field.onChange}}
      />
    </Form.Item>
    `;
  },
  // 枚举
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.ENUM.value]: (name, rule, field) => {
    let extraItem = '';
    if (Array.isArray(field.extInfo?.extraKey)) {
      extraItem = field.extInfo?.extraKey.map(key => {
        const extraKeyName = replaceFieldName(name, key);
        return `
          <Form.Item hidden={true} className='mb0' field='${extraKeyName}'>
            <Input disabled={true} size='small' />
          </Form.Item>
        `;
      });
    }
    const extraKeyLabel = replaceFieldName(name, 'Name');
    return `
    ${extraItem}
    <Form.Item hidden={true} className='mb0' field='${extraKeyLabel}'>
      <Input disabled={true} size='small' />
    </Form.Item>
    <Form.Item rules={${rule}} className='mb0 lx-enum-item lx-enum-${name}' field='${name}'>
      <EnumSelector
        name='${name}'
        pKey='${field.enumCode}'
        onChange={${field.onChange}}
        values='${JSON.stringify(field.values || [])}'
        defaultValue={${JSON.stringify(field.extInfo?.defaultValue)}}
        isLeaf={${field.extInfo?.isLeaf}}
        disabled={${field?.disabled}}
        bordered={false}
        isExpandAll={${field.extInfo?.isExpandAll}}
        isFullPath={${field.extInfo?.isFullPath}}
      />
    </Form.Item>
  `;
  },
  // 附件
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.FILE.value]: (name, rule, field) => {
    // 只有当 extInfo?.accept 真存在时才拼 accept 属性，
    // 否则拼出 accept="undefined" 字符串会让 arco 的 isAcceptFile 全过滤掉文件
    const acceptAttr = field?.extInfo?.accept ? ` accept="${field.extInfo.accept}"` : '';
    return `
    <Form.Item
      rules={${rule}}
      className='mb0 p5 lx-files-item'
      field='${name}'
    >
      <FilesUpload${acceptAttr} />
    </Form.Item>`;
  },
  // 单选
  [RULE_TYPES.RADIO.value]: (name, rule, field, id) => {
    return `<Form.Item rules={${rule}} className='mb0 lx-radio' field='${name}'><RadioGroup name='${name}' disabled={${field.disabled}} id='${id}' field={${JSON.stringify(field)}} onChange={${field?.onChange}} /></Form.Item>`;
  }
};

// 查看组件映射

export const viewMap = {
  // 文本
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.STRING.value]: (data, name) => {
    return `<>${data[name] || ''}</>`;
  },
  // 人名
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.NAME.value]: (data, name) => {
    const nameKey = replaceFieldName(name, 'Name');
    const unitKey = replaceFieldName(name, 'Unit');
    return `<>${data[nameKey] || ''}${data[unitKey] || ''}</>`;
  },
  // 人名图片
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.NI.value]: (data, name) => {
    // const nameKey = replaceFieldName(name, 'Name');
    // const unitKey = replaceFieldName(name, 'Unit');
    // return `<>${data[nameKey] || ''}${data[unitKey] || ''}</>`;
    if (data[name]) {
      const src = getUserSignatureUrl(data[name]);
      // return `
      // <img
      //   class='lx-user-name'
      //   src="${src}"
      //   width="70"
      //   height="30"
      // />`;
      return `
      <Image
        className="lx-user-signature"
        src='${src}'
        width={56}
        height={24}
      />
      `;
    }
    return `<></>`;
  },
  // 列表
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.LIST.value]: (data, name) => {
    const nameKey = replaceFieldName(name, 'Name');
    const unitKey = replaceFieldName(name, 'Unit');
    return `<>${data[nameKey] || ''}${data[unitKey] || ''}</>`;
  },
  // 枚举
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.ENUM.value]: (data, name) => {
    const nameKey = replaceFieldName(name, 'Name');
    const unitKey = replaceFieldName(name, 'Unit');
    return `<>${data[nameKey] || ''}${data[unitKey] || ''}</>`;
  },
  // 文件
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [RULE_TYPES.FILE.value]: (data, name) => {
    return `<FilesUpload isStatic={true} value={${JSON.stringify(data[name])}} />`;
  },
  // 单选框
  [RULE_TYPES.RADIO.value]: (data, name, field, id = '') => {
    const item = field.values?.find(item => item.label === data[name]) || {};
    if (id === item?.value) {
      return `<RadioIcon />`;
    } else {
      return `<EmptyCheckBoxIcon />`;
    }
  }
};